<script setup lang="ts">
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { api } from '@/api';
import { routenames } from '@/router/routenames';
import EnumerationForm from './components/EnumerationForm.vue';
import { useLoading } from '@/utils/useLoading';
import { onMounted } from 'vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import {
  type EnumerationQuantityDto,
  type CreateUpdateEnumerationQuantityDto,
  EnumerationQuantityDtoTypeEnum,
} from '@/api/generator';
import { useToken } from '@/utils/tokens/tokens';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { entityNotFoundErrorHandler, formBackendErrorHandler } from '@/errorHandling';
import { useRouter } from 'vue-router';
import { toCreateUpdateEnumerationQuantityDto } from './mappings';
import { translateEntityProperty } from '@/utils/helper.ts';
import { backendValidator } from '@/validation/validators/backendValidator';

export interface EnumerationEditViewProps {
  id: string;
}

const props = defineProps<EnumerationEditViewProps>();
const router = useRouter();

const { form } = useVeeForm<EnumerationQuantityDto>({
  schemaName: 'EnumerationQuantityDto',
  mode: 'edit',
  customValidateFunctions: [
    backendValidator((enumerationQuantityDto, errorHandlers) =>
      api.enumerationQuantitiesApi.validateEnumerationQuantity(
        {
          createUpdateEnumerationQuantityDto:
            toCreateUpdateEnumerationQuantityDto(enumerationQuantityDto),
        },
        errorHandlers,
      ),
    ),
  ],
});

const { action: loadEnumeration, loading } = useLoading(async () => {
  const { result } = await api.enumerationQuantitiesApi.getEnumerationQuantity(
    {
      id: props.id,
    },
    [entityNotFoundErrorHandler(router)],
  );

  if (result) {
    form.resetForm({ values: result });
  }
}, true);

const { values, handleSubmit } = form;

const token = useToken({
  id: props.id,
  $type:
    EnumerationQuantityDtoTypeEnum.PtwPtw100AppZeroApplicationSharedDataTypeManagementDtoEnumerationDtoEnumerationQuantityDtoPtwPtw100AppZeroApplicationShared,
});

onMounted(async () => token.request());

token.on.initialized(async () => loadEnumeration());

token.on.inUse(() => {
  router.push({
    name: routenames.enumerations.overview,
  });
});

const submit = handleSubmit(
  async (enumerationQuantity) => {
    // remove empty options before submit when usage is disabled
    [enumerationQuantity.evaluationUsage, enumerationQuantity.setupUsage].forEach((usage) => {
      if (!usage?.isUsageEnabled && usage?.enumerationOptions) {
        usage.enumerationOptions = usage.enumerationOptions.filter((option) => !!option.name);
      }
    });

    const createUpdateEnumerationQuantityDto: CreateUpdateEnumerationQuantityDto =
      toCreateUpdateEnumerationQuantityDto(enumerationQuantity);

    const { success } = await api.enumerationQuantitiesApi.updateEnumerationQuantity(
      {
        id: props.id,
        createUpdateEnumerationQuantityDto,
      },
      [formBackendErrorHandler(form)],
    );

    if (success) {
      await router.push({
        name: routenames.enumerations.overview,
      });
    }
  },
  {
    withReferenceCheck: true,
  },
);
</script>

<template>
  <form @submit="submit">
    <BasePage :loading="loading">
      <template #title>
        <BasePageTitle
          :subtitle="$t('Enumeration_one')"
          :title="translateEntityProperty('name', values)"
          :titleId="values.iuid"
        />
      </template>

      <template #status>
        <BasePageStatus :entity="values" />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.enumerations.overview }" />
        <VeeSubmitButton class="ml-4" />
      </template>

      <EnumerationForm />
    </BasePage>
  </form>
</template>
